// src/pages/finance-portal/recurring-transactions/RecurringTransactions.js
import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Paper,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    CircularProgress,
    Switch,
    FormControlLabel,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Skeleton,
} from '@mui/material';
import RepeatIcon from '@mui/icons-material/Repeat';
import SyncIcon from '@mui/icons-material/Sync';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import FilterListIcon from '@mui/icons-material/FilterList';
import { useSnackbar } from 'notistack';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#22c55e';

const FREQUENCY_CONFIG = {
    WEEKLY: { color: '#3b82f6', label: 'Weekly' },
    BIWEEKLY: { color: '#0ea5e9', label: 'Biweekly' },
    SEMI_MONTHLY: { color: '#06b6d4', label: 'Semi-Monthly' },
    MONTHLY: { color: '#22c55e', label: 'Monthly' },
    ANNUALLY: { color: '#8b5cf6', label: 'Annually' },
    UNKNOWN: { color: '#94a3b8', label: 'Unknown' },
};

function formatCurrency(amount, currency = 'USD') {
    if (amount == null) return '—';
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency || 'USD',
    }).format(amount);
}

function formatDate(dateStr) {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    });
}

export default function RecurringTransactions() {
    const { enqueueSnackbar } = useSnackbar();

    const [recurring, setRecurring] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [syncing, setSyncing] = useState(false);

    // Filters
    const [activeOnly, setActiveOnly] = useState(false);
    const [frequencyFilter, setFrequencyFilter] = useState('');

    const fetchRecurring = useCallback(async () => {
        setIsLoading(true);
        try {
            const params = {};
            if (activeOnly) params.active = true;
            if (frequencyFilter) params.frequency = frequencyFilter;

            const response = await axiosInstance.get('/api/finance/stripe/recurring', { params });
            if (response.data.success) {
                setRecurring(response.data.data || []);
            }
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || 'Failed to load recurring transactions', { variant: 'error' });
        } finally {
            setIsLoading(false);
        }
    }, [activeOnly, frequencyFilter, enqueueSnackbar]);

    useEffect(() => {
        fetchRecurring();
    }, [fetchRecurring]);

    const handleSync = async () => {
        setSyncing(true);
        try {
            const response = await axiosInstance.post('/api/finance/stripe/recurring/sync', {});
            if (response.data.success) {
                enqueueSnackbar(`Recurring transactions synced successfully`, { variant: 'success' });
                fetchRecurring();
            }
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || 'Sync failed', { variant: 'error' });
        } finally {
            setSyncing(false);
        }
    };

    const filteredRecurring = recurring;

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            {/* Header */}
            <Paper
                elevation={0}
                sx={{
                    background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                    borderRadius: { xs: 0, md: '0 0 24px 24px' },
                    px: { xs: 2.5, md: 4 },
                    py: { xs: 3, md: 4 },
                    mb: 3,
                    position: 'relative',
                    overflow: 'hidden',
                }}
            >
                <Box sx={{ position: 'absolute', top: -30, right: -30, width: 160, height: 160, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.06)' }} />
                <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ p: 1.5, borderRadius: '14px', bgcolor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <RepeatIcon sx={{ fontSize: 28, color: '#fff' }} />
                        </Box>
                        <Box>
                            <Typography variant="h4" sx={{ fontFamily, fontWeight: 800, color: 'white', fontSize: { xs: '1.4rem', md: '1.85rem' }, lineHeight: 1.2 }}>
                                Recurring Transactions
                            </Typography>
                            <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem', mt: 0.25 }}>
                                Detected recurring charges and income
                            </Typography>
                        </Box>
                    </Box>
                    <Button
                        variant="contained"
                        startIcon={syncing ? <CircularProgress size={16} color="inherit" /> : <SyncIcon />}
                        onClick={handleSync}
                        disabled={syncing}
                        sx={{
                            fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '12px',
                            bgcolor: 'rgba(255,255,255,0.2)', color: '#fff',
                            '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                            backdropFilter: 'blur(10px)',
                        }}
                    >
                        {syncing ? 'Syncing...' : 'Sync'}
                    </Button>
                </Box>
            </Paper>

            <Box sx={{ px: { xs: 2, md: 4 }, pb: 4, maxWidth: 1400, mx: 'auto' }}>
                {/* Filters */}
                <Paper sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none', p: 2.5, mb: 2.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <FilterListIcon sx={{ fontSize: 18, color: '#64748b' }} />
                        <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '0.85rem', color: '#64748b' }}>Filters</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={activeOnly}
                                    onChange={(e) => setActiveOnly(e.target.checked)}
                                    sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: ACCENT }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: ACCENT } }}
                                />
                            }
                            label={<Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#64748b' }}>Active Only</Typography>}
                        />
                        <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 160 } }}>
                            <InputLabel sx={{ fontFamily }}>Frequency</InputLabel>
                            <Select
                                value={frequencyFilter}
                                label="Frequency"
                                onChange={(e) => setFrequencyFilter(e.target.value)}
                                sx={{ fontFamily, borderRadius: '10px' }}
                            >
                                <MenuItem value="">All Frequencies</MenuItem>
                                {Object.entries(FREQUENCY_CONFIG).map(([key, cfg]) => (
                                    <MenuItem key={key} value={key} sx={{ fontFamily }}>{cfg.label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                </Paper>

                {/* Table */}
                <Paper sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none', overflow: 'hidden' }}>
                    <TableContainer sx={{ overflowX: 'auto' }}>
                        <Table>
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                    {['Merchant', 'Frequency', 'Avg Amount', 'Last Amount', 'Last Date', 'Active', 'Status'].map(h => (
                                        <TableCell key={h} sx={{ fontFamily, fontWeight: 700, fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid #e2e8f0', ...(h === 'Avg Amount' || h === 'Status' ? { display: { xs: 'none', md: 'table-cell' } } : {}) }}>
                                            {h}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {isLoading ? (
                                    [...Array(6)].map((_, i) => (
                                        <TableRow key={i}>
                                            {[...Array(7)].map((_, j) => (
                                                <TableCell key={j}><Skeleton variant="text" sx={{ fontFamily }} /></TableCell>
                                            ))}
                                        </TableRow>
                                    ))
                                ) : filteredRecurring.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={7} sx={{ textAlign: 'center', py: 6 }}>
                                            <RepeatIcon sx={{ fontSize: 40, color: '#cbd5e1', mb: 1 }} />
                                            <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.9rem' }}>No recurring transactions detected</Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    filteredRecurring.map((item) => {
                                        const freqConfig = FREQUENCY_CONFIG[item.frequency] || FREQUENCY_CONFIG.UNKNOWN;

                                        return (
                                            <TableRow key={item.id} sx={{ '&:hover': { bgcolor: '#f0fdf4' } }}>
                                                <TableCell sx={{ fontFamily, fontWeight: 600, fontSize: '0.85rem', color: '#1e293b', borderBottom: '1px solid #f1f5f9' }}>
                                                    {item.merchant_name || item.description || '—'}
                                                </TableCell>
                                                <TableCell sx={{ borderBottom: '1px solid #f1f5f9' }}>
                                                    <Chip
                                                        label={freqConfig.label}
                                                        size="small"
                                                        sx={{
                                                            fontFamily, fontSize: '0.7rem', fontWeight: 600, height: 24,
                                                            bgcolor: `${freqConfig.color}15`, color: freqConfig.color,
                                                        }}
                                                    />
                                                </TableCell>
                                                <TableCell sx={{ fontFamily, fontWeight: 600, fontSize: '0.85rem', color: '#1e293b', borderBottom: '1px solid #f1f5f9', display: { xs: 'none', md: 'table-cell' } }}>
                                                    {formatCurrency(item.average_amount)}
                                                </TableCell>
                                                <TableCell sx={{ fontFamily, fontWeight: 600, fontSize: '0.85rem', color: '#1e293b', borderBottom: '1px solid #f1f5f9' }}>
                                                    {formatCurrency(item.last_amount)}
                                                </TableCell>
                                                <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#475569', borderBottom: '1px solid #f1f5f9', whiteSpace: 'nowrap' }}>
                                                    {formatDate(item.last_date)}
                                                </TableCell>
                                                <TableCell sx={{ borderBottom: '1px solid #f1f5f9' }}>
                                                    {item.is_active ? (
                                                        <CheckCircleIcon sx={{ fontSize: 20, color: ACCENT }} />
                                                    ) : (
                                                        <CancelIcon sx={{ fontSize: 20, color: '#94a3b8' }} />
                                                    )}
                                                </TableCell>
                                                <TableCell sx={{ borderBottom: '1px solid #f1f5f9', display: { xs: 'none', md: 'table-cell' } }}>
                                                    <Chip
                                                        label={item.status || 'Detected'}
                                                        size="small"
                                                        sx={{
                                                            fontFamily, fontSize: '0.7rem', fontWeight: 600, height: 22,
                                                            bgcolor: item.status === 'MATURE' ? '#dcfce7' : item.status === 'EARLY_DETECTION' ? '#fef3c7' : '#f1f5f9',
                                                            color: item.status === 'MATURE' ? '#166534' : item.status === 'EARLY_DETECTION' ? '#92400e' : '#64748b',
                                                        }}
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            </Box>
        </Box>
    );
}
